import React, { useState, useEffect, useCallback } from 'react';
import {
  getReferenceData,
  getTimesheetLines,
  addTimesheetLine,
  deleteTimesheetLine,
  updateEntry,
  copyFromPreviousMonth,
  getVacations,
  getMonthStatus,
  getMonthHolidays,
  closeMonth,
  changePassword
} from '../api';
import { useLayout } from '../LayoutContext';

function TimesheetPage({ user, onLogout, onAdmin }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const { isMobile } = useLayout();
  const [lines, setLines] = useState([]);
  const [refData, setRefData] = useState({ costCenters: [], customers: [], jobTypes: [], jobIds: [] });
  const [vacationData, setVacationData] = useState({ monthVacations: [], summary: {} });
  const [holidays, setHolidays] = useState([]);
  const [monthStatus, setMonthStatus] = useState('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add line form state
  const [selectedCostCenter, setSelectedCostCenter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMsg, setPasswordMsg] = useState('');

  const daysInMonth = new Date(year, month, 0).getDate();
  const isClosed = monthStatus === 'closed';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [linesData, vacData, statusData, holidaysData] = await Promise.all([
        getTimesheetLines(month, year),
        getVacations(month, year),
        getMonthStatus(month, year),
        getMonthHolidays(month, year)
      ]);
      setLines(linesData);
      setVacationData(vacData);
      setMonthStatus(statusData.status);
      setHolidays(holidaysData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    getReferenceData().then(setRefData).catch(console.error);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredCustomers = refData.customers.filter(c => {
    if (!selectedCostCenter) return true;
    return c.cost_center_id === parseInt(selectedCostCenter);
  });

  const filteredJobTypes = refData.jobTypes.filter(jt => {
    if (!selectedCustomer) return true;
    // Show job types that have at least one job_id linked to the selected customer
    return refData.jobIds.some(j => j.customer_id === parseInt(selectedCustomer) && j.job_type_id === jt.id);
  });

  const filteredJobIds = refData.jobIds.filter(job => {
    if (selectedCostCenter && job.cost_center_id !== parseInt(selectedCostCenter)) return false;
    if (selectedCustomer && job.customer_id !== parseInt(selectedCustomer)) return false;
    if (selectedJobType && job.job_type_id !== parseInt(selectedJobType)) return false;
    return true;
  });

  const handleCostCenterChange = (value) => {
    setSelectedCostCenter(value);
    setSelectedCustomer('');
    setSelectedJobType('');
    setSelectedJobId('');
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomer(value);
    setSelectedJobType('');
    setSelectedJobId('');
  };

  const handleJobTypeChange = (value) => {
    setSelectedJobType(value);
    setSelectedJobId('');
  };

  const handleAddLine = async () => {
    if (!selectedJobId) return;
    try {
      const newLine = await addTimesheetLine(parseInt(selectedJobId), month, year);
      setLines([...lines, newLine]);
      setSelectedJobId('');
      setShowAddForm(false);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Delete this timesheet line?')) return;
    try {
      await deleteTimesheetLine(lineId);
      setLines(lines.filter(l => l.id !== lineId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleHoursChange = async (lineId, day, value) => {
    const hours = parseFloat(value) || 0;
    try {
      await updateEntry(lineId, day, hours);
      setLines(lines.map(l => {
        if (l.id === lineId) {
          const entries = { ...l.entries };
          if (hours === 0) {
            delete entries[day];
          } else {
            entries[day] = hours;
          }
          return { ...l, entries };
        }
        return l;
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyPrevious = async () => {
    try {
      const updatedLines = await copyFromPreviousMonth(month, year);
      setLines(updatedLines);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCloseMonth = async () => {
    if (!window.confirm(`Close ${monthNames[month - 1]} ${year}? You will no longer be able to edit this month's timesheet.`)) return;
    try {
      await closeMonth(month, year);
      setMonthStatus('closed');
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMsg('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordMsg('All fields are required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('New password must be at least 6 characters');
      return;
    }
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMsg('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowPasswordForm(false); setPasswordMsg(''); }, 2000);
    } catch (err) {
      setPasswordMsg(err.message);
    }
  };

  const getDayName = (day) => {
    const date = new Date(year, month - 1, day);
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
  };

  const isWeekend = (day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const getHolidayForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays.find(h => h.holiday_date === dateStr);
  };

  const getLineTotalHours = (line) => {
    return Object.values(line.entries).reduce((sum, h) => sum + h, 0);
  };

  const getDayTotalHours = (day) => {
    return lines.reduce((sum, line) => sum + (line.entries[day] || 0), 0);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="timesheet-container">
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <h1>Timesheet</h1>
        </div>
        <div className="header-right">
          {user.role === 'admin' && <button onClick={onAdmin} className="btn-secondary">Admin</button>}
          <button onClick={() => setShowPasswordForm(!showPasswordForm)} className="btn-secondary">
            Change Password
          </button>
          <span className="user-name">{user.full_name}</span>
          <button onClick={onLogout} className="btn-logout">Logout</button>
        </div>
      </header>

      {/* Change Password Form */}
      {showPasswordForm && (
        <div className="password-form-card">
          <h3>Change Password</h3>
          <div className="password-form-row">
            <input
              type="password"
              placeholder="Current Password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            />
            <input
              type="password"
              placeholder="New Password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            />
            <button onClick={handleChangePassword} className="btn-primary" style={{ width: 'auto' }}>Save</button>
            <button onClick={() => { setShowPasswordForm(false); setPasswordMsg(''); }} className="btn-secondary">Cancel</button>
          </div>
          {passwordMsg && <div className={`password-msg ${passwordMsg.includes('successfully') ? 'success' : 'error'}`}>{passwordMsg}</div>}
        </div>
      )}

      {/* Month/Year Selector */}
      <div className="controls-bar">
        <div className="month-selector">
          <button onClick={() => {
            if (month === 1) { setMonth(12); setYear(year - 1); }
            else { setMonth(month - 1); }
          }} className="btn-nav" title="Previous month">◀</button>
          <label htmlFor="month-select">Month:</label>
          <select
            id="month-select"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
          >
            {monthNames.map((name, i) => (
              <option key={i + 1} value={i + 1}>{name}</option>
            ))}
          </select>
          <label htmlFor="year-select">Year:</label>
          <select
            id="year-select"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={() => {
            if (month === 12) { setMonth(1); setYear(year + 1); }
            else { setMonth(month + 1); }
          }} className="btn-nav" title="Next month">▶</button>
        </div>
        <div className="action-buttons">
          {isClosed ? (
            <span className="month-status-badge closed">Month Closed</span>
          ) : (
            <>
              <button onClick={() => setShowAddForm(!showAddForm)} className="btn-secondary">
                {showAddForm ? 'Cancel' : '+ Add Job ID'}
              </button>
              <button onClick={handleCopyPrevious} className="btn-secondary">
                Copy from Previous Month
              </button>
              <button onClick={handleCloseMonth} className="btn-close-month">
                Close Month
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-banner" role="alert">{error}</div>}

      {/* Add Line Form */}
      {showAddForm && (
        <div className="add-line-form">
          <h3>Add Job ID Line</h3>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="filter-cc">Cost Center</label>
              <select id="filter-cc" value={selectedCostCenter} onChange={(e) => handleCostCenterChange(e.target.value)}>
                <option value="">All</option>
                {[...refData.costCenters].sort((a, b) => `${a.code} - ${a.name}`.localeCompare(`${b.code} - ${b.name}`)).map(cc => (
                  <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="filter-customer">Customer</label>
              <select id="filter-customer" value={selectedCustomer} onChange={(e) => handleCustomerChange(e.target.value)}>
                <option value="">All</option>
                {[...filteredCustomers].sort((a, b) => a.customer_name.localeCompare(b.customer_name)).map(c => (
                  <option key={c.id} value={c.id}>{c.customer_acronym} - {c.customer_name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="filter-type">Type</label>
              <select id="filter-type" value={selectedJobType} onChange={(e) => handleJobTypeChange(e.target.value)}>
                <option value="">All</option>
                {[...filteredJobTypes].sort((a, b) => a.type_id.localeCompare(b.type_id)).map(jt => (
                  <option key={jt.id} value={jt.id}>{jt.type_id} - {jt.type_description}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="filter-jobid">Job ID</label>
              <select id="filter-jobid" value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)}>
                <option value="">Select a Job ID</option>
                {[...filteredJobIds].sort((a, b) => a.job_id_code.localeCompare(b.job_id_code)).map(j => (
                  <option key={j.id} value={j.id}>{j.job_id_code} - {j.description}</option>
                ))}
              </select>
            </div>
            <button onClick={handleAddLine} className="btn-primary" disabled={!selectedJobId}>
              Add
            </button>
          </div>
        </div>
      )}

      {/* Vacation Summary */}
      <div className="vacation-summary">
        <h3>Vacation Summary ({monthNames[month - 1]} {year})</h3>
        <div className="vacation-stats">
          <div className="stat">
            <span className="stat-label">Annual Allowance</span>
            <span className="stat-value">{vacationData.summary.totalAllowanceDays || 0} days ({vacationData.summary.totalAllowanceHours || 0}h)</span>
          </div>
          <div className="stat">
            <span className="stat-label">Taken (YTD)</span>
            <span className="stat-value">
              {(() => {
                // Calculate taken from G_VACATION lines up to current month
                const vacLine = lines.find(l => l.job_id_code === 'G_VACATION');
                const takenThisMonth = vacLine ? Object.values(vacLine.entries).reduce((s, h) => s + h, 0) : 0;
                const takenDays = (vacationData.summary.takenHours || 0) / 8;
                return `${takenDays.toFixed(1)} days (${vacationData.summary.takenHours || 0}h)`;
              })()}
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Remaining</span>
            <span className="stat-value">
              {(() => {
                const remaining = vacationData.summary.remainingDays || 0;
                const remainingHours = vacationData.summary.remainingHours || 0;
                return `${remaining.toFixed ? remaining.toFixed(1) : remaining} days (${remainingHours}h)`;
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* Timesheet Grid — desktop table or mobile card view */}
      {loading ? (
        <div className="loading">Loading timesheet...</div>
      ) : isMobile ? (
        /* ── MOBILE CARD VIEW ── */
        lines.length === 0 ? (
          <div className="empty-state">
            <p>No timesheet lines for {monthNames[month - 1]} {year}.</p>
            <p>Add a job ID or copy from the previous month to get started.</p>
          </div>
        ) : (
          <div className="ts-card-list">
            {lines
              .filter(l => l.job_id_code !== 'G_HOLIDAY') // holidays shown as cell markers
              .map(line => {
                const isVac = line.job_id_code === 'G_VACATION';
                const totalHours = getLineTotalHours(line);
                return (
                  <div key={line.id} className={`ts-card${isVac ? ' vacation-row' : ''}`}>
                    <div className={`ts-card-header${isVac ? ' vacation-row' : ''}`}>
                      <div>
                        <div className="ts-card-title">{line.job_id_code}</div>
                        <div className="ts-card-subtitle">{line.description}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="ts-card-total">{totalHours}h</span>
                        {!isClosed && !isVac && (
                          <button
                            className="ts-card-delete"
                            onClick={() => handleDeleteLine(line.id)}
                            title="Delete line"
                            aria-label={`Delete ${line.job_id_code}`}
                          >×</button>
                        )}
                      </div>
                    </div>
                    <div className="ts-card-body">
                      <div className="ts-days-grid">
                        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                          const weekend = isWeekend(day);
                          const hol = getHolidayForDay(day);
                          const holLine = lines.find(l => l.job_id_code === 'G_HOLIDAY');
                          const isHol = !!(holLine && holLine.entries[day]);
                          const hasHours = !!(line.entries[day]);
                          let cellClass = 'ts-day-cell';
                          if (weekend) cellClass += ' weekend';
                          if (isHol) cellClass += ' holiday';
                          if (isVac && hasHours) cellClass += ' vacation';
                          if (hasHours && !isVac) cellClass += ' has-hours';
                          return (
                            <div key={day} className={cellClass}>
                              <span className="ts-day-label">{getDayName(day)}</span>
                              <span className="ts-day-num">{day}</span>
                              <input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                className="ts-day-input"
                                value={line.entries[day] || ''}
                                onChange={(e) => handleHoursChange(line.id, day, e.target.value)}
                                disabled={isClosed || isVac}
                                aria-label={`${line.job_id_code} day ${day}`}
                                title={hol ? hol.holiday_name : undefined}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

            {/* Mobile daily totals card */}
            <div className="ts-card">
              <div className="ts-card-header">
                <div className="ts-card-title">Daily Totals</div>
                <span className="ts-card-total">
                  {lines.reduce((s, l) => s + getLineTotalHours(l), 0)}h
                </span>
              </div>
              <div className="ts-card-body">
                <div className="ts-days-grid">
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const total = getDayTotalHours(day);
                    return (
                      <div key={day} className={`ts-day-cell${isWeekend(day) ? ' weekend' : ''}${total ? ' has-hours' : ''}`}>
                        <span className="ts-day-label">{getDayName(day)}</span>
                        <span className="ts-day-num">{day}</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: total ? 700 : 400, color: total ? '#667eea' : '#ccc', textAlign: 'center', minHeight: '1.2rem' }}>
                          {total || '–'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        /* ── DESKTOP TABLE VIEW ── */
        <>
          <div className="timesheet-grid-wrapper">
            <table className="timesheet-grid" role="grid" aria-label="Timesheet entries">
              <thead>
                <tr>
                  <th className="sticky-col col-actions">Actions</th>
                  <th className="sticky-col col-jobid">Job ID</th>
                  <th className="sticky-col col-desc">Description</th>
                  <th className="sticky-col col-total">Total</th>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                    <th key={day} className={`day-header ${isWeekend(day) ? 'weekend' : ''}`}>
                      <div>{day}</div>
                      <div className="day-name">{getDayName(day)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map(line => (
                  <tr key={line.id}>
                    <td className="sticky-col col-actions">
                      {!isClosed && (
                        <button
                          onClick={() => handleDeleteLine(line.id)}
                          className="btn-delete"
                          title="Delete line"
                          aria-label={`Delete ${line.job_id_code}`}
                        >
                          ×
                        </button>
                      )}
                    </td>
                    <td className="sticky-col col-jobid">{line.job_id_code}</td>
                    <td className="sticky-col col-desc" title={line.description}>{line.description}</td>
                    <td className="sticky-col col-total">{getLineTotalHours(line)}</td>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                      <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''}`}>
                        <input
                          type="number"
                          min="0"
                          max="24"
                          step="0.5"
                          value={line.entries[day] || ''}
                          onChange={(e) => handleHoursChange(line.id, day, e.target.value)}
                          className="hours-input"
                          disabled={isClosed}
                          aria-label={`Hours for ${line.job_id_code} on day ${day}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Daily totals row */}
                <tr className="totals-row">
                  <td className="sticky-col col-actions"></td>
                  <td className="sticky-col col-jobid"><strong>Daily Total</strong></td>
                  <td className="sticky-col col-desc"></td>
                  <td className="sticky-col col-total">
                    <strong>{lines.reduce((sum, l) => sum + getLineTotalHours(l), 0)}</strong>
                  </td>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                    <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''}`}>
                      <strong>{getDayTotalHours(day) || ''}</strong>
                    </td>
                  ))}
                </tr>
                {/* Vacation row */}
                <tr className="vacation-row">
                  <td className="sticky-col col-actions"></td>
                  <td className="sticky-col col-jobid"><em>Vacation</em></td>
                  <td className="sticky-col col-desc"></td>
                  <td className="sticky-col col-total">
                    <em>{(() => {
                      const vacLine = lines.find(l => l.job_id_code === 'G_VACATION');
                      return vacLine ? Object.values(vacLine.entries).reduce((s, h) => s + h, 0) : 0;
                    })()}h</em>
                  </td>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const vacLine = lines.find(l => l.job_id_code === 'G_VACATION');
                    const hours = vacLine && vacLine.entries[day] ? vacLine.entries[day] : 0;
                    return (
                      <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''} ${hours ? 'vacation-day' : ''}`}>
                        {hours ? `${hours}h` : ''}
                      </td>
                    );
                  })}
                </tr>
                {/* Holidays row */}
                <tr className="holiday-row">
                  <td className="sticky-col col-actions"></td>
                  <td className="sticky-col col-jobid"><em>Holidays</em></td>
                  <td className="sticky-col col-desc"></td>
                  <td className="sticky-col col-total">
                    <em>{(() => {
                      const holLine = lines.find(l => l.job_id_code === 'G_HOLIDAY');
                      return holLine ? Object.values(holLine.entries).reduce((s, h) => s + h, 0) : 0;
                    })()}h</em>
                  </td>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const hol = getHolidayForDay(day);
                    const holLine = lines.find(l => l.job_id_code === 'G_HOLIDAY');
                    const hours = holLine && holLine.entries[day] ? holLine.entries[day] : 0;
                    return (
                      <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''} ${hol ? 'holiday-day' : ''}`} title={hol ? hol.holiday_name : ''}>
                        {hours ? `${hours}h` : ''}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {lines.length === 0 && !loading && (
            <div className="empty-state">
              <p>No timesheet lines for {monthNames[month - 1]} {year}.</p>
              <p>Add a job ID or copy from the previous month to get started.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TimesheetPage;
